import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchPurchaseOrdersApi,
  fetchPurchaseOrderApi,
  fetchPoStatsApi,
  fetchPoFiltersDataApi,
  updatePurchaseOrderApi,
  createPurchaseOrderApi,
  approvePoApi,
  rejectPoApi,
  rejectAtrApi,
  submitPoApi,
  unsubmitPoApi,
  cancelPoApi,
  approveCancelPoApi,
  rejectCancelPoApi,
  recreatePoApi,
  deletePoApi,
  fulfillPoApi,
  splitPoApi,
  mergePoApi,
  bulkApprovePoApi,
  bulkRejectPoApi,
  syncJobCardsApi,
  syncTechniciansApi,
  createTbaJobcardsApi,
  sendSupplierApi,
  bulkSetTypeApi,
  bulkSetQuotationVatApi,
  bulkSetItemOrderApi,
  bulkMarkNonVatApi,
  bulkSubmitPaymentApi,
  receiveStockApi,
  undoReceiveApi,
  bulkSendToMetrofileApi,
  bulkExportToMetrofileApi,
  saveQuotationApi,
  getCreditNotesApi,
  createCreditNoteApi,
  updateCreditNoteApi,
  deleteCreditNoteApi,
  addCreditNoteLineItemApi,
  deleteCreditNoteLineItemApi,
  getAmendmentsApi,
  createAmendmentApi,
  approveAmendmentApi,
  rejectAmendmentApi,
  getPoItemsApi,
  addPoItemApi,
  updatePoItemApi,
  deletePoItemApi,
  pullFromAtrApi,
  getOrderDeliverySummaryApi,
  downloadSageExportApi,
  getGrnsApi,
  getGrnPoItemsApi,
  createGrnApi,
  getGrnApi,
  updateGrnApi,
  finalizeGrnApi,
  deleteGrnApi,
  approveAllVariancesApi,
  PoListParams,
} from '../../services/api';
import type { RootState } from '../../app/store';

// ==================== Types ====================

export interface PoMinimalStore {
  id: number;
  name: string;
  storenumber?: string;
  area_id?: number | null;
  regional_id?: number | null;
}

export interface PoMinimalUser {
  id: number;
  name: string;
  email?: string;
}

export interface PoStatus {
  id: number;
  name: string;
}

export interface PoFulfillment {
  id: number;
  name: string;
}

export interface PoApprover {
  id: number;
  user_id: number;
  user: PoMinimalUser;
  status_id: number;
  comment?: string;
  tba?: boolean;
}

export interface PoItem {
  id: number;
  item_code?: string;
  name?: string;
  description: string;
  quantity: number;
  price: number;
  unit_of_measure?: string;
  non_vat?: boolean;
  vat?: string;
}

export interface PoQuotation {
  id: number;
  quotation_number?: string;
  amount: number;
  vat?: boolean | number;
  attachment?: string;
  date?: string;
}

export interface PoInvoice {
  id: number;
  invoice_number?: string;
  amount: number | string;
  date?: string;
  attachment?: string;
  vat?: boolean | number;
}

export interface PoHistoryEntry {
  id: number;
  user_id: number;
  user?: PoMinimalUser;
  action: string;
  comment?: string;
  created_at: string;
}

export interface PoGrn {
  id: number;
  grn_number?: string;
  status?: string;
  created_at?: string;
}

export interface PoJobcard {
  id: number;
  job_number?: string;
  attachment?: string;
  created_at?: string;
}

export interface PoTicket {
  id: number;
  comment?: string;
  attachment?: string;
  created_at?: string;
}

export interface PoDelivery {
  id: number;
  delivery_number?: string;
  received_by?: string;
  attachment?: string;
  date?: string;
  created_at?: string;
}

export interface PoOther {
  id: number;
  title?: string;
  description?: string;
  attachment?: string;
  created_at?: string;
}

export interface PoListItem {
  id: number;
  order_number?: string;
  reference_number?: string;
  date: string;
  status_id: number;
  submitted: boolean | number;
  internal?: number;
  is_repair?: number;
  is_order?: boolean;
  is_linked?: boolean;
  parent_id?: number | null;
  non_vat?: boolean;
  required_jobcard?: number;
  stores: PoMinimalStore[];
  user: PoMinimalUser;
  status?: PoStatus;
  fulfillment?: PoFulfillment;
  supplier?: { id: number; name: string };
  technician?: PoMinimalUser;
  requisition?: { id: number } | null;
  quotation?: PoQuotation;
  approvers?: PoApprover[];
  items?: PoItem[];
  grns?: PoGrn[];
  invoices?: PoInvoice[];
  category?: { id: number; name: string };
  subCategory?: { id: number; name: string };
  updated_at: string;
  created_at?: string;
  deliverynotes_count?: number;
  jobcards_count?: number;
  invoices_count?: number;
  creditnotes_count?: number;
  tickets_count?: number;
  others_count?: number;
  is_owner?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  can_select?: boolean;
  can_cancel?: boolean;
  can_recreate?: boolean;
  can_approve_cancel?: boolean;
  can_reject_cancel?: boolean;
  cancel_index?: number | boolean;
  recreate?: number | boolean;
  grn_status?: 'not_received' | 'received_fully' | 'partially_received' | null;
  is_amended?: boolean;
  documents_required?: string | null;
  non_vat_display?: boolean;
  is_fulfilled?: boolean;
}

export interface PoDetail extends PoListItem {
  requested_by?: number;
  requestedBy?: PoMinimalUser;
  histories?: PoHistoryEntry[];
  invoices?: PoInvoice[];
  deliverynotes?: PoDelivery[];
  creditnotes?: any[];
  jobcards?: PoJobcard[];
  others?: PoOther[];
  tickets?: any[];
  category?: { id: number; name: string };
  subCategory?: { id: number; name: string };
  department?: { id: number; name: string };
  amendments?: any[];
  parent?: PoListItem | null;
  children?: PoListItem[];
  documentDeliveries?: any[];
  missingDocuments?: any[];
  equipmentRequests?: any[];
  assets?: any[];
  repairs?: any[];
  // Derived attributes
  original_total_amount?: number;
  current_total_amount?: number;
  total_with_amendments?: number;
  can_be_amended?: boolean;
  is_linked_order?: boolean;
  linked_family?: PoListItem[];
  equipment_items?: any[];
  asset_description_pairs?: any[];
  can_approve?: boolean;
  can_reject?: boolean;
  can_submit?: boolean;
  can_unsubmit?: boolean;
}

export interface PoStatusCounts {
  pending: number;
  submitted_pending: number;
  tba: number;
  approved: number;
  rejected: number;
  cancelled: number;
}

export interface PoFiltersData {
  statuses: PoStatus[];
  stores: PoMinimalStore[];
  suppliers: { id: number; name: string }[];
  technicians: PoMinimalUser[];
  users: PoMinimalUser[];
  approvers: PoMinimalUser[];
  fulfillments: PoFulfillment[];
  areaCoaches: PoMinimalUser[];
  regionals: PoMinimalUser[];
  categories: { id: number; name: string }[];
  subcategories: { id: number; name: string; requisition_category_id: number }[];
}

// ==================== State ====================

interface PoState {
  list: PoListItem[];
  current: PoDetail | null;
  statusCounts: PoStatusCounts | null;
  filtersData: PoFiltersData | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  filters: PoListParams;
  selectedIds: number[];
}

const initialState: PoState = {
  list: [],
  current: null,
  statusCounts: null,
  filtersData: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
  currentPage: 1,
  lastPage: 1,
  total: 0,
  perPage: 20,
  filters: {
    per_page: 20,
    page: 1,
  },
  selectedIds: [],
};

// ==================== Thunks ====================

export const fetchPurchaseOrders = createAsyncThunk(
  'po/fetchList',
  async (params: PoListParams | undefined, { rejectWithValue }) => {
    try {
      const response = await fetchPurchaseOrdersApi(params || {});
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch purchase orders');
    }
  }
);

export const fetchPurchaseOrderDetail = createAsyncThunk(
  'po/fetchDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await fetchPurchaseOrderApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch purchase order');
    }
  }
);

export const fetchPoStats = createAsyncThunk(
  'po/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPoStatsApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

export const fetchPoFiltersData = createAsyncThunk(
  'po/fetchFiltersData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPoFiltersDataApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch filters data');
    }
  }
);

export const updatePurchaseOrder = createAsyncThunk(
  'po/update',
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await updatePurchaseOrderApi(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update PO');
    }
  }
);

export const createPurchaseOrder = createAsyncThunk(
  'po/create',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await createPurchaseOrderApi(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create PO');
    }
  }
);

export const approvePurchaseOrder = createAsyncThunk(
  'po/approve',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await approvePoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve');
    }
  }
);

export const rejectPurchaseOrder = createAsyncThunk(
  'po/reject',
  async ({ id, reason }: { id: number; reason: string }, { rejectWithValue }) => {
    try {
      const response = await rejectPoApi(id, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject');
    }
  }
);

export const submitPurchaseOrder = createAsyncThunk(
  'po/submit',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await submitPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit');
    }
  }
);

export const unsubmitPurchaseOrder = createAsyncThunk(
  'po/unsubmit',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await unsubmitPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unsubmit');
    }
  }
);

export const rejectAtrPurchaseOrder = createAsyncThunk(
  'po/rejectAtr',
  async ({ id, comment }: { id: number; comment: string }, { rejectWithValue }) => {
    try {
      const response = await rejectAtrApi(id, comment);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject ATR');
    }
  }
);

export const cancelPurchaseOrder = createAsyncThunk(
  'po/cancel',
  async ({ id, reason, recreate }: { id: number; reason: string; recreate?: boolean }, { rejectWithValue }) => {
    try {
      const response = await cancelPoApi(id, reason, recreate);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel');
    }
  }
);

export const approveCancelPurchaseOrder = createAsyncThunk(
  'po/approveCancel',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await approveCancelPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve cancellation');
    }
  }
);

export const rejectCancelPurchaseOrder = createAsyncThunk(
  'po/rejectCancel',
  async ({ id, reason }: { id: number; reason: string }, { rejectWithValue }) => {
    try {
      const response = await rejectCancelPoApi(id, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject cancellation');
    }
  }
);

export const recreatePurchaseOrder = createAsyncThunk(
  'po/recreate',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await recreatePoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to recreate order');
    }
  }
);

export const deletePurchaseOrder = createAsyncThunk(
  'po/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await deletePoApi(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete');
    }
  }
);

export const fulfillPurchaseOrder = createAsyncThunk(
  'po/fulfill',
  async ({ id, fulfillmentId }: { id: number; fulfillmentId: number }, { rejectWithValue }) => {
    try {
      const response = await fulfillPoApi(id, fulfillmentId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update fulfillment');
    }
  }
);

export const splitPurchaseOrder = createAsyncThunk(
  'po/split',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await splitPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to split');
    }
  }
);

export const mergePurchaseOrders = createAsyncThunk(
  'po/merge',
  async ({ mainOrderId, orderIds }: { mainOrderId: number; orderIds: number[] }, { rejectWithValue }) => {
    try {
      const response = await mergePoApi(mainOrderId, orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to merge');
    }
  }
);

export const bulkApprovePurchaseOrders = createAsyncThunk(
  'po/bulkApprove',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkApprovePoApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk approve');
    }
  }
);

export const bulkRejectPurchaseOrders = createAsyncThunk(
  'po/bulkReject',
  async ({ orderIds, reason }: { orderIds: number[]; reason: string }, { rejectWithValue }) => {
    try {
      const response = await bulkRejectPoApi(orderIds, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk reject');
    }
  }
);

// ==================== Sync & Admin Thunks ====================

export const syncJobCards = createAsyncThunk(
  'po/syncJobCards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await syncJobCardsApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to sync job cards');
    }
  }
);

export const syncTechnicians = createAsyncThunk(
  'po/syncTechnicians',
  async (_, { rejectWithValue }) => {
    try {
      const response = await syncTechniciansApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to sync technicians');
    }
  }
);

export const createTbaJobcards = createAsyncThunk(
  'po/createTbaJobcards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await createTbaJobcardsApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create TBA jobcards');
    }
  }
);

export const sendSupplier = createAsyncThunk(
  'po/sendSupplier',
  async ({ id, tbaApprover }: { id: number; tbaApprover: number }, { rejectWithValue }) => {
    try {
      const response = await sendSupplierApi(id, tbaApprover);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send to supplier');
    }
  }
);

export const bulkSetType = createAsyncThunk(
  'po/bulkSetType',
  async ({ orderIds, isRepair }: { orderIds: number[]; isRepair: number }, { rejectWithValue }) => {
    try {
      const response = await bulkSetTypeApi(orderIds, isRepair);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set type');
    }
  }
);

export const bulkSetQuotationVat = createAsyncThunk(
  'po/bulkSetQuotationVat',
  async ({ orderIds, isInclusive }: { orderIds: number[]; isInclusive: number }, { rejectWithValue }) => {
    try {
      const response = await bulkSetQuotationVatApi(orderIds, isInclusive);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set quotation VAT');
    }
  }
);

export const bulkSetItemOrder = createAsyncThunk(
  'po/bulkSetItemOrder',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkSetItemOrderApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set item order');
    }
  }
);

export const bulkMarkNonVat = createAsyncThunk(
  'po/bulkMarkNonVat',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkMarkNonVatApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark as Non-VAT');
    }
  }
);

export const bulkSubmitPayment = createAsyncThunk(
  'po/bulkSubmitPayment',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkSubmitPaymentApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit for payment');
    }
  }
);

export const receiveStock = createAsyncThunk(
  'po/receiveStock',
  async ({ id, receivedAt }: { id: number; receivedAt: string }, { rejectWithValue }) => {
    try {
      const response = await receiveStockApi(id, receivedAt);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to receive stock');
    }
  }
);

export const undoReceive = createAsyncThunk(
  'po/undoReceive',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await undoReceiveApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to undo receive');
    }
  }
);

export const bulkSendToMetrofile = createAsyncThunk(
  'po/bulkSendToMetrofile',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkSendToMetrofileApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send to Metrofile');
    }
  }
);

export const bulkExportToMetrofile = createAsyncThunk(
  'po/bulkExportToMetrofile',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkExportToMetrofileApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export to Metrofile');
    }
  }
);

export const saveQuotation = createAsyncThunk(
  'po/saveQuotation',
  async ({ id, data }: { id: number; data: { quotation_date?: string; quotation_number?: string; repair_cost?: number; vat?: number } }, { rejectWithValue }) => {
    try {
      const response = await saveQuotationApi(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save quotation');
    }
  }
);

// ==================== Credit Notes Thunks ====================

export const fetchCreditNotes = createAsyncThunk(
  'po/fetchCreditNotes',
  async (poId: number, { rejectWithValue }) => {
    try {
      const response = await getCreditNotesApi(poId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch credit notes');
    }
  }
);

export const createCreditNote = createAsyncThunk(
  'po/createCreditNote',
  async ({ poId, data }: { poId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await createCreditNoteApi(poId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create credit note');
    }
  }
);

export const updateCreditNote = createAsyncThunk(
  'po/updateCreditNote',
  async ({ poId, creditNoteId, data }: { poId: number; creditNoteId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await updateCreditNoteApi(poId, creditNoteId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update credit note');
    }
  }
);

export const deleteCreditNote = createAsyncThunk(
  'po/deleteCreditNote',
  async ({ poId, creditNoteId }: { poId: number; creditNoteId: number }, { rejectWithValue }) => {
    try {
      const response = await deleteCreditNoteApi(poId, creditNoteId);
      return { ...response, creditNoteId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete credit note');
    }
  }
);

export const addCreditNoteLineItem = createAsyncThunk(
  'po/addCreditNoteLineItem',
  async ({ poId, creditNoteId, data }: { poId: number; creditNoteId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await addCreditNoteLineItemApi(poId, creditNoteId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add line item');
    }
  }
);

export const removeCreditNoteLineItem = createAsyncThunk(
  'po/removeCreditNoteLineItem',
  async ({ poId, creditNoteId, lineItemId }: { poId: number; creditNoteId: number; lineItemId: number }, { rejectWithValue }) => {
    try {
      const response = await deleteCreditNoteLineItemApi(poId, creditNoteId, lineItemId);
      return { ...response, lineItemId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove line item');
    }
  }
);

// ==================== PO Amendments Thunks ====================

export const fetchAmendments = createAsyncThunk(
  'po/fetchAmendments',
  async (poId: number, { rejectWithValue }) => {
    try {
      const response = await getAmendmentsApi(poId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch amendments');
    }
  }
);

export const createAmendment = createAsyncThunk(
  'po/createAmendment',
  async ({ poId, data }: { poId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await createAmendmentApi(poId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create amendment');
    }
  }
);

export const approveAmendment = createAsyncThunk(
  'po/approveAmendment',
  async ({ poId, amendmentId }: { poId: number; amendmentId: number }, { rejectWithValue }) => {
    try {
      const response = await approveAmendmentApi(poId, amendmentId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve amendment');
    }
  }
);

export const rejectAmendment = createAsyncThunk(
  'po/rejectAmendment',
  async ({ poId, amendmentId, reason }: { poId: number; amendmentId: number; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await rejectAmendmentApi(poId, amendmentId, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject amendment');
    }
  }
);

// ==================== Manage Items Thunks ====================

export const fetchPoItems = createAsyncThunk(
  'po/fetchPoItems',
  async (poId: number, { rejectWithValue }) => {
    try {
      const response = await getPoItemsApi(poId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch items');
    }
  }
);

export const addPoItem = createAsyncThunk(
  'po/addPoItem',
  async ({ poId, data }: { poId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await addPoItemApi(poId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add item');
    }
  }
);

export const updatePoItem = createAsyncThunk(
  'po/updatePoItem',
  async ({ poId, itemId, data }: { poId: number; itemId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await updatePoItemApi(poId, itemId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update item');
    }
  }
);

export const deletePoItem = createAsyncThunk(
  'po/deletePoItem',
  async ({ poId, itemId }: { poId: number; itemId: number }, { rejectWithValue }) => {
    try {
      const response = await deletePoItemApi(poId, itemId);
      return { ...response, itemId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete item');
    }
  }
);

export const loadAtrItems = createAsyncThunk(
  'po/loadAtrItems',
  async ({ poId, keepExisting }: { poId: number; keepExisting: boolean }, { rejectWithValue }) => {
    try {
      const response = await pullFromAtrApi(poId, keepExisting);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load ATR items');
    }
  }
);

// ==================== Order Delivery Summary Thunk ====================

export const fetchOrderDeliverySummary = createAsyncThunk(
  'po/fetchOrderDeliverySummary',
  async (poId: number, { rejectWithValue }) => {
    try {
      const response = await getOrderDeliverySummaryApi(poId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch delivery summary');
    }
  }
);

// ==================== SAGE Export Thunk ====================

export const downloadSageExport = createAsyncThunk(
  'po/downloadSageExport',
  async (params: any | undefined, { rejectWithValue }) => {
    try {
      const blob = await downloadSageExportApi(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sage_purchase_orders_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate AP export');
    }
  }
);

// ==================== GRN Management Thunks ====================

export const fetchGrns = createAsyncThunk(
  'po/fetchGrns',
  async (poRequestId: number, { rejectWithValue }) => {
    try {
      const response = await getGrnsApi(poRequestId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch GRNs');
    }
  }
);

export const fetchGrnPoItems = createAsyncThunk(
  'po/fetchGrnPoItems',
  async (poRequestId: number, { rejectWithValue }) => {
    try {
      const response = await getGrnPoItemsApi(poRequestId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch PO items');
    }
  }
);

export const createGrn = createAsyncThunk(
  'po/createGrn',
  async ({ poRequestId, data }: { poRequestId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await createGrnApi(poRequestId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create GRN');
    }
  }
);

export const finalizeGrn = createAsyncThunk(
  'po/finalizeGrn',
  async (grnId: number, { rejectWithValue }) => {
    try {
      const response = await finalizeGrnApi(grnId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to finalize GRN');
    }
  }
);

export const removeGrn = createAsyncThunk(
  'po/removeGrn',
  async (grnId: number, { rejectWithValue }) => {
    try {
      const response = await deleteGrnApi(grnId);
      return { ...response, grnId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete GRN');
    }
  }
);

export const fetchGrnDetail = createAsyncThunk(
  'po/fetchGrnDetail',
  async (grnId: number, { rejectWithValue }) => {
    try {
      const response = await getGrnApi(grnId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch GRN details');
    }
  }
);

export const updateGrn = createAsyncThunk(
  'po/updateGrn',
  async ({ grnId, data }: { grnId: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await updateGrnApi(grnId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update GRN');
    }
  }
);

export const approveAllVariances = createAsyncThunk(
  'po/approveAllVariances',
  async (grnId: number, { rejectWithValue }) => {
    try {
      const response = await approveAllVariancesApi(grnId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve variances');
    }
  }
);

// ==================== Slice ====================

const poSlice = createSlice({
  name: 'purchaseOrders',
  initialState,
  reducers: {
    setPoFilters: (state, action: PayloadAction<Partial<PoListParams>>) => {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    resetPoFilters: (state) => {
      state.filters = { per_page: 20, page: 1 };
    },
    setPoPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    toggleSelectedId: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((sid) => sid !== id);
      } else {
        state.selectedIds = [...state.selectedIds, id];
      }
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    clearCurrentPo: (state) => {
      state.current = null;
    },
    clearPoError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.currentPage = action.payload.meta.current_page;
        state.lastPage = action.payload.meta.last_page;
        state.total = action.payload.meta.total;
        state.perPage = action.payload.meta.per_page;
        if (action.payload.status_counts) {
          state.statusCounts = action.payload.status_counts;
        }
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch detail
      .addCase(fetchPurchaseOrderDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrderDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.current = action.payload.data;
      })
      .addCase(fetchPurchaseOrderDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload as string;
      })
      // Fetch stats
      .addCase(fetchPoStats.fulfilled, (state, action) => {
        state.statusCounts = action.payload.data;
      })
      // Fetch filters data
      .addCase(fetchPoFiltersData.fulfilled, (state, action) => {
        state.filtersData = action.payload.data;
      })
      // Create PO
      .addCase(createPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
        }
      })
      .addCase(createPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Update PO
      .addCase(updatePurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updatePurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
        }
      })
      .addCase(updatePurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Approve
      .addCase(approvePurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(approvePurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(approvePurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Reject
      .addCase(rejectPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(rejectPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Submit
      .addCase(submitPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(submitPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(submitPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Unsubmit
      .addCase(unsubmitPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(unsubmitPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(unsubmitPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Reject ATR
      .addCase(rejectAtrPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectAtrPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.po) {
          state.current = action.payload.po;
        }
        if (state.list.length > 0) {
          state.list = state.list.map(po =>
            po.id === action.payload.po?.id ? { ...po, ...action.payload.po } : po
          );
        }
      })
      .addCase(rejectAtrPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Cancel
      .addCase(cancelPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(cancelPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(cancelPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Approve cancellation
      .addCase(approveCancelPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(approveCancelPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          if (state.current?.id === action.payload.data.id) {
            state.current = { ...state.current, ...action.payload.data, can_approve_cancel: false, can_reject_cancel: false };
          }
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? { ...po, ...action.payload.data } : po
          );
        }
      })
      .addCase(approveCancelPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Reject cancellation
      .addCase(rejectCancelPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectCancelPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          if (state.current?.id === action.payload.data.id) {
            state.current = { ...state.current, ...action.payload.data, can_approve_cancel: false, can_reject_cancel: false };
          }
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? { ...po, ...action.payload.data } : po
          );
        }
      })
      .addCase(rejectCancelPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Recreate
      .addCase(recreatePurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(recreatePurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        // The cancelled order can no longer be recreated
        state.list = state.list.map((po) =>
          po.id === action.meta.arg ? { ...po, can_recreate: false } : po
        );
        if (state.current && state.current.id === action.meta.arg) {
          state.current = { ...state.current, can_recreate: false };
        }
      })
      .addCase(recreatePurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deletePurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deletePurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.list = state.list.filter((po) => po.id !== action.payload.id);
        state.selectedIds = state.selectedIds.filter((id) => id !== action.payload.id);
      })
      .addCase(deletePurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Fulfill
      .addCase(fulfillPurchaseOrder.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.current = action.payload.data;
        }
      })
      // Split
      .addCase(splitPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(splitPurchaseOrder.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(splitPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Merge
      .addCase(mergePurchaseOrders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(mergePurchaseOrders.fulfilled, (state) => {
        state.actionLoading = false;
        state.selectedIds = [];
      })
      .addCase(mergePurchaseOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Bulk approve
      .addCase(bulkApprovePurchaseOrders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bulkApprovePurchaseOrders.fulfilled, (state) => {
        state.actionLoading = false;
        state.selectedIds = [];
      })
      .addCase(bulkApprovePurchaseOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Bulk reject
      .addCase(bulkRejectPurchaseOrders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bulkRejectPurchaseOrders.fulfilled, (state) => {
        state.actionLoading = false;
        state.selectedIds = [];
      })
      .addCase(bulkRejectPurchaseOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPoFilters,
  resetPoFilters,
  setPoPage,
  setSelectedIds,
  toggleSelectedId,
  clearSelectedIds,
  clearCurrentPo,
  clearPoError,
} = poSlice.actions;

export default poSlice.reducer;

// Selectors
export const selectPoList = (state: RootState) => state.purchaseOrders.list;
export const selectPoCurrent = (state: RootState) => state.purchaseOrders.current;
export const selectPoLoading = (state: RootState) => state.purchaseOrders.loading;
export const selectPoDetailLoading = (state: RootState) => state.purchaseOrders.detailLoading;
export const selectPoActionLoading = (state: RootState) => state.purchaseOrders.actionLoading;
export const selectPoError = (state: RootState) => state.purchaseOrders.error;
export const selectPoStatusCounts = (state: RootState) => state.purchaseOrders.statusCounts;
export const selectPoFiltersData = (state: RootState) => state.purchaseOrders.filtersData;
export const selectPoFilters = (state: RootState) => state.purchaseOrders.filters;
export const selectPoPagination = (state: RootState) => ({
  currentPage: state.purchaseOrders.currentPage,
  lastPage: state.purchaseOrders.lastPage,
  total: state.purchaseOrders.total,
  perPage: state.purchaseOrders.perPage,
});
export const selectSelectedIds = (state: RootState) => state.purchaseOrders.selectedIds;
